/* ImportPreviewModal — review AI-proposed field mappings + preview rows,
 * then commit. Optionally save as a reusable Profile for daily re-runs.
 *
 * v2 (Feb 2026): inverted mapping (Verqo fields → sheet column dropdowns),
 * sticky table headers, top-and-bottom horizontal scrollbar, required-
 * field guard with bright red borders.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import {
  CheckCircle2, AlertTriangle, ArrowRight, Sparkles, BookOpen, Eye,
  Pencil, X, Star, Asterisk,
} from "lucide-react";

// Canonical Verqo fields. `required: true` flags MUST be mapped before
// the seller can apply. Order = display order in the mapping list.
const CANONICAL_FIELDS = [
  { key: "sku",          label: "SKU",                 hint: "Unique product code",          required: true },
  { key: "name",         label: "Product name",        hint: "Customer-facing title",        required: true },
  { key: "price",        label: "Wholesale price",     hint: "What buyers pay you",          required: true },
  { key: "brand",        label: "Brand",               hint: "Manufacturer / line" },
  { key: "category",     label: "Category",            hint: "Beauty · Apparel · Home etc" },
  { key: "description",  label: "Description",         hint: "Free-text marketing copy" },
  { key: "retail_price", label: "Retail / MSRP",       hint: "Suggested resale price" },
  { key: "moq",          label: "MOQ",                 hint: "Minimum order quantity" },
  { key: "stock",        label: "Stock / inventory",   hint: "Units available" },
  { key: "case_size",    label: "Case size",           hint: "Units per case" },
  { key: "upc",          label: "UPC / EAN",           hint: "Barcode number" },
  { key: "image",        label: "Image URL",           hint: "Cover image link" },
  { key: "fulfillment",  label: "Fulfillment",         hint: "wholesale · dropship · both" },
  { key: "badge",        label: "Badge",               hint: "new · top · weekly · today's special" },
  { key: "map_price",    label: "MAP price",           hint: "Minimum advertised price" },
  { key: "tags",         label: "Tags",                hint: "Comma-separated search keywords" },
];

const REQUIRED_KEYS = CANONICAL_FIELDS.filter(f => f.required).map(f => f.key);

export default function ImportPreviewModal({ preview, onClose, onApply }) {
  const { job_id, detected_columns = [], detected_mappings = [], preview_rows, rows_total, used_ai } = preview;

  // ── Mapping state: keyed by CANONICAL field → source_column (or "")
  // Initial state inverts the AI's detected_mappings (which is the other
  // direction) into our canonical-first shape.
  const initialMap = useMemo(() => {
    const m = {};
    CANONICAL_FIELDS.forEach(f => { m[f.key] = ""; });
    (detected_mappings || []).forEach(d => {
      if (d.canonical_field && d.source_column && m.hasOwnProperty(d.canonical_field)) {
        // First wins — if AI guessed two source columns for the same
        // canonical field, keep the first (higher confidence usually
        // comes first from the parser).
        if (!m[d.canonical_field]) m[d.canonical_field] = d.source_column;
      }
    });
    return m;
  }, [detected_mappings]);

  const [mapping, setMapping] = useState(initialMap);
  // Editable copy of preview rows — the seller can fix any cell before applying.
  const [editableRows, setEditableRows] = useState(() => preview_rows ? preview_rows.slice(0, 50).map((r) => ({ ...r })) : []);
  const [editing, setEditing] = useState(null);   // { rowIdx, field } when a cell is active
  const [saveAsProfile, setSaveAsProfile] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [autoApply, setAutoApply] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");   // all | required | mapped | unmapped

  // ── Derived state
  // 1. fields actually mapped (i.e. will be written on import)
  const presentFields = useMemo(() => {
    const s = new Set();
    Object.entries(mapping).forEach(([canonical, src]) => { if (src) s.add(canonical); });
    return s;
  }, [mapping]);

  // 2. required fields still missing
  const missingRequired = REQUIRED_KEYS.filter(k => !mapping[k]);

  // 3. source columns that the seller HASN'T mapped to anything —
  //    surfaced so they can spot unused headers + map them.
  const mappedSources = useMemo(() => {
    return new Set(Object.values(mapping).filter(Boolean));
  }, [mapping]);
  const unusedColumns = (detected_columns || []).filter(c => !mappedSources.has(c));

  // 4. show fields filtered (search/segment)
  const visibleFields = useMemo(() => CANONICAL_FIELDS.filter(f => {
    if (filter === "required") return f.required;
    if (filter === "mapped")   return !!mapping[f.key];
    if (filter === "unmapped") return !mapping[f.key];
    return true;
  }), [filter, mapping]);

  // ── Cell editing
  const updateCell = (rowIdx, field, raw) => {
    setEditableRows((rows) => {
      const next = [...rows];
      const v = coerceField(field, raw);
      if (v === null || v === undefined || v === "") {
        const copy = { ...next[rowIdx] };
        delete copy[field];
        next[rowIdx] = copy;
      } else {
        next[rowIdx] = { ...next[rowIdx], [field]: v };
      }
      return next;
    });
  };
  const removeRow = (idx) => setEditableRows((rows) => rows.filter((_, i) => i !== idx));

  // ── Top-and-bottom synced horizontal scrollbar
  // The bottom scrollbar lives natively on the table's scroll container.
  // The "top" scrollbar is a fake div whose content width mirrors the
  // table — scrolling either one syncs the other.
  const topScrollRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [tableWidth, setTableWidth] = useState(0);
  useEffect(() => {
    if (!tableScrollRef.current) return;
    const measure = () => {
      const el = tableScrollRef.current;
      if (el) setTableWidth(el.scrollWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(tableScrollRef.current);
    // Children mutate, schedule another measure on next tick
    const t = setTimeout(measure, 100);
    return () => { ro.disconnect(); clearTimeout(t); };
  }, [editableRows, presentFields, mapping]);

  const syncFromTop = () => {
    if (topScrollRef.current && tableScrollRef.current) {
      tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  };
  const syncFromTable = () => {
    if (topScrollRef.current && tableScrollRef.current) {
      topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
  };

  // ── Submit
  const submit = async () => {
    if (missingRequired.length > 0) return;   // disabled button anyway
    setBusy(true);
    const mappings = Object.entries(mapping)
      .filter(([_canon, src]) => src)
      .map(([canonical_field, source_column]) => ({ source_column, canonical_field, confidence: 1.0 }));
    try {
      await onApply({
        jobId: job_id,
        mappings,
        rows: editableRows,
        saveAsProfile,
        profileName: profileName.trim() || `Profile ${new Date().toLocaleDateString()}`,
        autoApply,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="import-preview-modal">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="display text-2xl tracking-tight inline-flex items-center gap-2">
            <Eye className="w-5 h-5 text-foreground/55"/>
            Review &amp; apply
            {used_ai && (
              <span className="chip bg-foreground text-background text-[10px] inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3"/>AI-mapped
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-foreground/55">
            We detected <strong className="text-foreground">{rows_total}</strong> rows and <strong className="text-foreground">{(detected_columns || []).length}</strong> columns.
            Match each Verqo field to a column in your sheet, then preview the result.
          </DialogDescription>
        </DialogHeader>

        {/* Required-field guard banner */}
        {missingRequired.length > 0 && (
          <div className="mx-6 mb-3 rounded-lg bg-rose-50 border border-rose-200 px-4 py-2.5 inline-flex items-center gap-2 text-xs text-rose-800" data-testid="missing-required">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0"/>
            <span>
              Required field{missingRequired.length === 1 ? "" : "s"} not mapped yet:{" "}
              <strong>{missingRequired.map(k => labelFor(k)).join(", ")}</strong>. Pick the right column from your sheet to enable Apply.
            </span>
          </div>
        )}

        <div className="grid lg:grid-cols-[460px_1fr] gap-0 overflow-hidden flex-1 min-h-0">
          {/* MAPPING PANEL — inverted: Verqo field → sheet column dropdown */}
          <div className="overflow-y-auto p-6 pt-0 border-r border-border" data-testid="mapping-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55">
                Match Verqo fields to your sheet
              </div>
              <div className="inline-flex gap-1 text-[10px]" data-testid="mapping-filter">
                {[
                  { id: "all",       l: "All" },
                  { id: "required",  l: "Required" },
                  { id: "mapped",    l: `Mapped (${presentFields.size})` },
                  { id: "unmapped",  l: `Unmapped (${CANONICAL_FIELDS.length - presentFields.size})` },
                ].map(t => (
                  <button key={t.id} onClick={() => setFilter(t.id)}
                    data-testid={`mapping-filter-${t.id}`}
                    className={`px-2 py-1 rounded-full transition-colors ${filter === t.id ? "bg-foreground text-background" : "text-foreground/55 hover:text-foreground"}`}>
                    {t.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              {visibleFields.map((f) => {
                const src = mapping[f.key];
                const isMapped = !!src;
                const isMissingRequired = f.required && !isMapped;
                return (
                  <div key={f.key}
                    data-testid={`map-row-${f.key}`}
                    className={`rounded-xl border bg-white px-3 py-2.5 transition-colors ${
                      isMissingRequired ? "border-rose-300 bg-rose-50/30" :
                      isMapped ? "border-emerald-200 bg-emerald-50/20" :
                      "border-border"
                    }`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-[12.5px] font-medium flex items-center gap-1.5">
                        {f.label}
                        {f.required && <Asterisk className="w-2.5 h-2.5 text-rose-500" title="Required"/>}
                      </span>
                      {isMapped && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 ml-auto"/>}
                      {isMissingRequired && <span className="ml-auto chip bg-rose-100 border-rose-200 text-rose-700 text-[9px]">missing</span>}
                    </div>
                    <div className="text-[10.5px] text-foreground/50 mb-2">{f.hint}</div>
                    <select
                      value={src || ""}
                      onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}
                      data-testid={`map-select-${f.key}`}
                      className={`w-full h-9 rounded-md border bg-white px-2 text-xs focus:outline-none transition-colors mono ${
                        isMissingRequired ? "border-rose-300 focus:border-rose-500" : "border-border focus:border-foreground/40"
                      }`}>
                      <option value="">{f.required ? "— pick a column from your sheet —" : "— not in my sheet —"}</option>
                      {(detected_columns || []).map(col => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>

            {/* Unused source columns surface */}
            {unusedColumns.length > 0 && (
              <div className="mt-5 rounded-xl border border-dashed border-border bg-secondary/30 p-3" data-testid="unused-columns">
                <div className="text-[10px] mono uppercase tracking-wider text-foreground/55 mb-1.5">
                  Unused sheet columns ({unusedColumns.length})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {unusedColumns.map(c => (
                    <span key={c} className="chip mono text-[10px] bg-white border border-border text-foreground/60 truncate max-w-[180px]" title={c}>{c}</span>
                  ))}
                </div>
                <div className="mt-2 text-[10.5px] text-foreground/50 leading-relaxed">
                  These columns won&apos;t be imported. If any of them belong to a Verqo field, map them above.
                </div>
              </div>
            )}
          </div>

          {/* PREVIEW PANEL */}
          <div className="flex flex-col bg-secondary/30 overflow-hidden min-h-0">
            <div className="flex items-center justify-between px-6 pt-5 pb-2.5">
              <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55">
                Preview · {editableRows.length} row{editableRows.length !== 1 ? "s" : ""} of {rows_total}
              </div>
              <div className="text-[10.5px] text-foreground/55 inline-flex items-center gap-1">
                <Pencil className="w-3 h-3"/>Click any cell to edit
              </div>
            </div>

            {/* TOP horizontal scrollbar — fake, syncs to the table below */}
            <div className="mx-6 rounded-t-xl border border-b-0 border-border bg-white overflow-x-auto"
              data-testid="preview-top-scroll"
              ref={topScrollRef}
              onScroll={syncFromTop}
              style={{ height: 14 }}>
              <div style={{ width: tableWidth, height: 1 }}/>
            </div>

            {/* TABLE — single x+y scroll container so sticky thead works */}
            <div className="mx-6 mb-3 rounded-b-xl border border-t-0 border-border bg-white overflow-auto flex-1 min-h-0"
              data-testid="preview-table-scroll"
              ref={tableScrollRef}
              onScroll={syncFromTable}>
              <table className="text-[12px] border-separate border-spacing-0"
                style={{ minWidth: Math.max(720, ([...presentFields].length + 1) * 150) + "px" }}>
                <thead className="bg-secondary z-20">
                  <tr>
                    <th className="w-9 px-2 py-2 sticky top-0 left-0 bg-secondary z-30 border-b border-r border-border"></th>
                    {[...presentFields].map(f => (
                      <th key={f} className="text-left px-3 py-2 text-[10px] uppercase tracking-wider mono text-foreground/55 whitespace-nowrap min-w-[150px] sticky top-0 bg-secondary border-b border-border z-20">
                        {labelFor(f)}
                        <div className="text-[9px] normal-case text-foreground/40 font-normal">from {mapping[f] || "—"}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {editableRows.map((r, i) => (
                    <tr key={i} className="hover:bg-secondary/40 group" data-testid={`preview-row-${i}`}>
                      <td className="px-2 py-1.5 text-center sticky left-0 bg-white z-10 border-b border-r border-border group-hover:bg-secondary/40">
                        <button onClick={() => removeRow(i)} className="w-5 h-5 rounded-md text-foreground/35 hover:text-rose-600 hover:bg-rose-50 grid place-items-center" title="Remove this row" data-testid={`preview-remove-${i}`}>
                          <X className="w-3 h-3"/>
                        </button>
                      </td>
                      {[...presentFields].map(f => {
                        const active = editing && editing.rowIdx === i && editing.field === f;
                        const value = r[f];
                        return (
                          <td key={f}
                            className={`px-3 py-1.5 cursor-text min-w-[150px] max-w-[260px] whitespace-nowrap overflow-hidden text-ellipsis border-b border-border ${active ? "bg-amber-50" : ""}`}
                            onClick={() => setEditing({ rowIdx: i, field: f })}
                            data-testid={`cell-${i}-${f}`}>
                            {active ? (
                              <input
                                autoFocus
                                defaultValue={value === null || value === undefined ? "" : (Array.isArray(value) ? value.join(", ") : String(value))}
                                onBlur={(e) => { updateCell(i, f, e.target.value); setEditing(null); }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter")  { updateCell(i, f, e.target.value); setEditing(null); }
                                  if (e.key === "Escape") setEditing(null);
                                }}
                                className="w-full h-7 px-1.5 text-[12px] mono border border-amber-300 rounded bg-white focus:outline-none focus:border-amber-500"
                                data-testid={`cell-input-${i}-${f}`}
                              />
                            ) : renderCell(f, value)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {editableRows.length === 0 && (
                    <tr><td colSpan={presentFields.size + 1} className="px-4 py-10 text-center text-foreground/45 border-b border-border">No preview rows</td></tr>
                  )}
                  {presentFields.size === 0 && editableRows.length > 0 && (
                    <tr><td className="px-4 py-10 text-center text-foreground/45 border-b border-border" colSpan={1}>
                      Map at least one field on the left to see a preview.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {editableRows.length < rows_total && (
              <div className="px-6 pb-3 text-[10.5px] text-foreground/55">
                Showing first {editableRows.length} of {rows_total} rows. The mapping you confirm here will be applied to all {rows_total}.
              </div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="border-t border-border bg-white px-6 py-4 flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={saveAsProfile} onChange={(e) => setSaveAsProfile(e.target.checked)} className="accent-foreground" data-testid="save-profile-toggle"/>
            <BookOpen className="w-3.5 h-3.5 text-foreground/55"/>
            Save mapping as a profile
          </label>
          {saveAsProfile && (
            <>
              <Input value={profileName} onChange={(e) => setProfileName(e.target.value)}
                placeholder="e.g. Daily Stock Update"
                className="w-56 h-9" data-testid="profile-name-input"/>
              <label className="inline-flex items-center gap-2 text-[11px] cursor-pointer">
                <input type="checkbox" checked={autoApply} onChange={(e) => setAutoApply(e.target.checked)} className="accent-foreground"/>
                Auto-apply on email/webhook inbound
              </label>
            </>
          )}
          <div className="ml-auto flex gap-2 items-center">
            {missingRequired.length === 0 && presentFields.size > 0 && (
              <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1" data-testid="ready-to-apply">
                <CheckCircle2 className="w-3 h-3"/>{presentFields.size} field{presentFields.size === 1 ? "" : "s"} mapped — ready
              </span>
            )}
            <Button variant="outline" onClick={onClose} className="rounded-full h-10 px-4">Cancel</Button>
            <Button onClick={submit}
              disabled={busy || editableRows.length === 0 || missingRequired.length > 0}
              data-testid="apply-btn"
              className="rounded-full h-10 px-5 bg-foreground text-background hover:bg-foreground/90 disabled:opacity-40">
              {busy ? "Applying…" : <>Apply {rows_total || editableRows.length} row{(rows_total || editableRows.length) !== 1 ? "s" : ""} to catalog <ArrowRight className="w-3.5 h-3.5 ml-1.5"/></>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function labelFor(key) {
  const f = CANONICAL_FIELDS.find(x => x.key === key);
  return f ? f.label : key;
}

function coerceField(field, raw) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (s === "") return null;
  if (["price", "retail_price", "map_price"].includes(field)) {
    const n = parseFloat(s.replace(/[$,]/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  if (["moq", "stock", "case_size"].includes(field)) {
    const n = parseInt(s.replace(/[,]/g, ""), 10);
    return Number.isFinite(n) ? n : null;
  }
  if (field === "image") {
    // Strict — must be a URL. Anything else is description text leaking
    // in from a misnamed source column. Reject so the apply doesn't
    // silently store description text in the image field.
    if (/^https?:\/\//i.test(s) || s.startsWith("/api/catalog/images/")) return s;
    return null;
  }
  if (field === "tags") {
    return s.split(/[,;|]/).map(t => t.trim()).filter(Boolean);
  }
  return s;
}

function renderCell(field, v) {
  if (v === null || v === undefined || v === "") return <span className="text-foreground/30">—</span>;
  if (field === "price" || field === "retail_price" || field === "map_price") return <span className="mono">${Number(v).toFixed(2)}</span>;
  if (field === "moq" || field === "stock" || field === "case_size") return <span className="mono">{v}</span>;
  if (field === "image" && typeof v === "string" && v.startsWith("http")) return <a href={v} target="_blank" rel="noreferrer" className="underline text-[11px]" onClick={(e) => e.stopPropagation()}>link</a>;
  if (Array.isArray(v)) return <span className="text-[11px]">{v.slice(0, 3).join(", ")}</span>;
  return <span className="text-[12px]">{String(v)}</span>;
}
